import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CompareProvider } from "./contexts/CompareContext";
import CompareBar from "./components/CompareBar";
import InstallPrompt from "./components/InstallPrompt";
import { LOCALES, detectBrowserLocale, pathnameLocale, buildPath } from "./lib/i18n";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { captureUtmFromUrl, trackPageview } from "./lib/analytics";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Listings from "./pages/Listings";
import ListingDetail from "./pages/ListingDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CreateListing from "./pages/CreateListing";
import Dashboard from "./pages/Dashboard";
import Favorites from "./pages/Favorites";
import Messages from "./pages/Messages";
import Pricing from "./pages/Pricing";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import MapView from "./pages/MapView";
import SavedSearches from "./pages/SavedSearches";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import Dealers from "./pages/Dealers";
import DealerDetail from "./pages/DealerDetail";
import DealerProfile from "./pages/DealerProfile";
import Compare from "./pages/Compare";
import CompareSeoPage from "./pages/CompareSeoPage";
import CompareHubPage from "./pages/CompareHubPage";
import BrandSeoPage from "./pages/BrandSeoPage";
import { ImprintPage, PrivacyPage, TermsPage } from "./pages/Legal";
import Contact from "./pages/Contact";

// Redirect "/" or unprefixed paths to the user's preferred locale.
function RootRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = buildPath(detectBrowserLocale(), pathname) + search + hash;
  return <Navigate to={target} replace />;
}

// Layout that renders Navbar/Footer once the locale is established
function LocaleLayout() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Validate that the URL locale is supported; redirect unknown locales to default
  useEffect(() => {
    const cur = pathnameLocale(pathname);
    if (cur && !LOCALES.includes(cur)) {
      navigate(buildPath(detectBrowserLocale(), pathname), { replace: true });
    }
  }, [pathname, navigate]);

  // Phase 64 — capture UTM params on first hit, then track every SPA route change
  useEffect(() => { captureUtmFromUrl(); }, []);
  useEffect(() => {
    // Defer one tick so useSeoHead has updated document.title first
    const t = setTimeout(() => trackPageview({ path: pathname + (search || "") }), 30);
    return () => clearTimeout(t);
  }, [pathname, search]);

  // Phase 66 — scroll restoration. On SPA route changes the browser keeps
  // the previous scroll position, which is wrong for top-level navigation.
  // We jump back to top unless the URL has a hash anchor (#section).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return; // honour anchor links
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <LanguageProvider>
      <AuthProvider>
        <CompareProvider>
          <div className="flex min-h-screen flex-col">
            <Navbar />
            <main className="flex-1">
              <Outlet />
            </main>
            <Footer />
            <CompareBar />
            <InstallPrompt />
            <CookieConsentBanner />
            <Toaster theme="dark" position="top-right" />
          </div>
        </CompareProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Locale-prefixed routes */}
        <Route path=":locale" element={<LocaleLayout />}>
          <Route index element={<Home />} />
          <Route path="listings" element={<Listings />} />
          <Route path="listings/:id" element={<ListingDetail />} />
          <Route path="listings/:id/edit" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="sell" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
          <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
          <Route path="map" element={<MapView />} />
          <Route path="saved-searches" element={<ProtectedRoute><SavedSearches /></ProtectedRoute>} />
          <Route path="admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="dealers" element={<Dealers />} />
          <Route path="dealers/:slug" element={<DealerDetail />} />
          <Route path="dealer/profile" element={<ProtectedRoute><DealerProfile /></ProtectedRoute>} />
          <Route path="compare" element={<Compare />} />
          <Route path="vergleich" element={<CompareHubPage />} />
          <Route path="comparaison" element={<CompareHubPage />} />
          <Route path="confronto" element={<CompareHubPage />} />
          <Route path="vergleich/:slug" element={<CompareSeoPage />} />
          <Route path="comparaison/:slug" element={<CompareSeoPage />} />
          <Route path="confronto/:slug" element={<CompareSeoPage />} />
          <Route path="compare/:slug" element={<CompareSeoPage />} />
          {/* Phase 55.1 — SEO Brand & Model landing pages */}
          <Route path="autos/:brand" element={<BrandSeoPage />} />
          <Route path="autos/:brand/:model" element={<BrandSeoPage />} />
          <Route path="voitures/:brand" element={<BrandSeoPage />} />
          <Route path="voitures/:brand/:model" element={<BrandSeoPage />} />
          <Route path="auto/:brand" element={<BrandSeoPage />} />
          <Route path="auto/:brand/:model" element={<BrandSeoPage />} />
          <Route path="cars/:brand" element={<BrandSeoPage />} />
          <Route path="cars/:brand/:model" element={<BrandSeoPage />} />
          <Route path="legal/imprint" element={<ImprintPage />} />
          <Route path="legal/privacy" element={<PrivacyPage />} />
          <Route path="legal/terms" element={<TermsPage />} />
          <Route path="contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="" replace />} />
        </Route>
        {/* Catch-all: redirect anything unprefixed to a locale */}
        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
